/**
 * iter-170c — useAdvisorAction hook extracted from AdvisorChat.jsx.
 *
 * Encapsulates the React Query mutation + toast wiring for approving
 * a suggested advisor action. Returns the mutation object plus
 * ``done``/``editing`` UI state so the caller stays slim (CC win).
 *
 * iter-170r — Consume backend ``nav_path`` to surface a persistent
 * "Ver detalle" action button on the success toast, so the user can
 * jump to the freshly-created resource without losing the chat thread.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { advisorService } from "../../services/advisorService";
import { tx } from "../../i18n";

export function useAdvisorAction({ action, lang }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({ ...action }));

  const exec = useMutation({
    mutationFn: (payload) => advisorService.executeAction(payload),
    onSuccess: (data) => {
      setDone(true);
      setEditing(false);
      const navPath = data?.nav_path;
      const message =
        tx(lang, "Saved to ", "Creado en ", "Criado em ") + (data?.saved_in || "—");
      toast.success(message, {
        // Persist the CTA long enough for the user to notice it; sonner
        // dismisses on click of either the action button or the X.
        duration: 8000,
        action: navPath
          ? {
              label: tx(lang, "View", "Ver detalle", "Ver detalhe"),
              onClick: () => navigate(navPath),
            }
          : undefined,
      });
      qc.invalidateQueries();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || "error";
      toast.error(
        tx(lang, "Approve failed: ", "No se pudo aprobar: ", "Falha ao aprovar: ") + detail,
      );
    },
  });

  return { done, editing, setEditing, draft, setDraft, exec };
}
